import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, Info, X } from "lucide-react";
import { format, startOfWeek, addDays, isToday, addMinutes } from "date-fns";
import { enUS } from "date-fns/locale";
import { getData } from "../../api/api";
import { useStorage } from "../../hooks/useLocalStorage";


// Component chính hiển thị lịch tương tác hàng tuần
export default function InteractiveWeeklySchedule() {
  // State lưu trạng thái của hoạt động được chọn
  const [selectedActivity, setSelectedActivity] = useState(null);
  // State lưu ngày hiện tại
  const [currentDate, setCurrentDate] = useState(new Date());
  // State lưu dữ liệu của lịch (sau khi tải từ API)
  const [scheduleData, setScheduleData] = useState(null);
  // Lấy `id` từ URL thông qua hook `useParams`
  const { id } = useParams();
  const [user, setUser, loadUser] = useStorage("use", null);
  // Hàm mở chi tiết hoạt động
  const openActivityDetails = (activity) => {
    setSelectedActivity(activity);
  };

  // Hàm đóng chi tiết hoạt động
  const closeActivityDetails = () => {
    setSelectedActivity(null);
  };

  // Tính ngày đầu tuần hiện tại (theo thứ hai)
  const startOfCurrentWeek = startOfWeek(currentDate, { weekStartsOn: 1 });
  // Tạo danh sách 7 ngày trong tuần
  const days = Array.from({ length: 7 }, (_, i) =>
    addDays(startOfCurrentWeek, i)
  );

  // Hàm tạo các khung giờ từ thời gian bắt đầu đến thời gian kết thúc theo khoảng cách cố định
  const generateTimeSlots = (startTime, endTime, interval) => {
    const start = new Date(`1970-01-01T${startTime}:00`); // Thời gian bắt đầu
    const end = new Date(`1970-01-01T${endTime}:00`); // Thời gian kết thúc
    const times = [];

    // Lặp để tạo danh sách thời gian
    for (let time = start; time < end; time = addMinutes(time, interval)) {
      times.push(format(time, "HH:mm")); // Định dạng thời gian thành chuỗi
    }

    return times;
  };

  // Định nghĩa các khung giờ cố định (3 giờ/lần từ 06:00 đến 21:00)
  const timeSlots = generateTimeSlots("06:00", "21:00", 180);

  // Tải dữ liệu lịch từ API khi `currentDate` hoặc `id` thay đổi
  useEffect(() => {
    getData(`/retreat-groups?retreatId=${id}&participantId=${user.id}&pageNumber=0&pageSize=10`)
      .then((response) => {
        const groups = response?.data?.data || [];
        if (groups.length > 0) {
          const groupId = groups[0].id;
          console.log("groupId",groupId);

          getData(`/group/schedule/?groupId=${groupId}`, )
            .then((scheduleResponse) => {
              const groupScheduleItems = scheduleResponse.data.data;
              const groupActivities = [];

              groupScheduleItems.forEach((item) => {
                const startTime = item.lessonStart.substring(0, 5);
                const endTime = item.lessonEnd ? item.lessonEnd.substring(0, 5) : "";
                const date = item.lessonDate;

                groupActivities.push({
                  id: item.id,
                  startTime: startTime,
                  endTime: endTime,
                  date: date,
                  name: item.lessonTitle,
                  details: item.lessonContent,
                  location: item.roomName  ,
                });
              });

              setScheduleData({
                timeSlots: timeSlots, // Thêm timeSlots tùy theo cấu trúc dữ liệu
                activities: groupActivities,
              });
            })
            .catch((error) => {
              console.error("Error fetching group schedule:", error);
            });
        }
      })
      .catch((error) => {
        console.error("Error fetching groups:", error);
      });
  }, [currentDate, id, user.id]);

  // Nếu dữ liệu chưa được tải, hiển thị "Loading..."
  if (!scheduleData) {
    return <div>Loading...</div>;
  }

  return (
    <div className="max-w-[1200px] mx-auto p-4 bg-[#f9f7f4]">
      {/* Tiêu đề lịch */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-medium text-[#725b53]">
          Basic Schedule for the Retreat Week from{" "}
          {format(startOfCurrentWeek, "dd.MM.yyyy")} -{" "}
          {format(addDays(startOfCurrentWeek, 6), "dd.MM.yyyy")}
        </h1>
        {/* Nút chuyển tuần */}
        <div className="flex items-center gap-2">
          <button
            className="p-2 hover:bg-gray-200 rounded"
            onClick={() => setCurrentDate(addDays(currentDate, -7))}
          >
            <ChevronLeft className="w-5 h-5 text-gray-600" />
          </button>
          <button
            className="p-2 hover:bg-gray-200 rounded"
            onClick={() => setCurrentDate(addDays(currentDate, 7))}
          >
            <ChevronRight className="w-5 h-5 text-gray-600" />
          </button>
        </div>
      </div>

      {/* Lưới hiển thị lịch */}
      <div className="overflow-x-auto">
        <div className="min-w-[900px]">
          {/* Header ngày trong tuần */}
          <div className="grid grid-cols-[150px_repeat(7,1fr)] bg-[#e8e1d9]">
            <div className="p-2 font-medium text-[#725b53] border-r border-[#d3c7bc]">
              Time
            </div>
            {days.map((day, index) => {
              const dayName = format(day, "EEEE", { locale: enUS }); // Tên ngày
              const dayNumber = format(day, "dd.MM.yyyy"); // Ngày
              const isTodayFlag = isToday(day); // Kiểm tra có phải hôm nay
              return (
                <div
                  key={index}
                  className={`p-2 font-medium text-center text-[#725b53] border-r border-[#d3c7bc] ${
                    isTodayFlag ? "bg-yellow-200" : ""
                  }`}
                >
                  {dayName} {dayNumber}
                </div>
              );
            })}
          </div>

          {/* Các khung giờ */}
          {scheduleData.timeSlots.map((time) => {
            const [slotHour, slotMinute] = time.split(":").map(Number);
            const timeSlotStartMinutes = slotHour * 60 + slotMinute;
            const timeSlotEndMinutes = timeSlotStartMinutes + 180; // Kết thúc sau 3 giờ
            const timeSlotEndTime = format(
              addMinutes(new Date(`1970-01-01T${time}:00`), 180),
              "HH:mm"
            );

            return (
              <div
                key={time}
                className="grid grid-cols-[150px_repeat(7,1fr)] border-b border-[#d3c7bc]"
              >
                <div className="p-2 text-sm text-[#725b53] border-r border-[#d3c7bc] bg-[#f3efe9]">
                  {time} - {timeSlotEndTime}
                </div>
                {days.map((day, index) => {
                  const dateStr = format(day, "yyyy-MM-dd");
                  const activitiesInTimeSlot = scheduleData.activities.filter(
                    (activity) => {
                      if (activity.date !== dateStr) {
                        return false;
                      }
                      const [activityHour, activityMinute] = activity.startTime
                        .split(":")
                        .map(Number);
                      const activityStartMinutes =
                        activityHour * 60 + activityMinute;
                      return (
                        activityStartMinutes >= timeSlotStartMinutes &&
                        activityStartMinutes < timeSlotEndMinutes
                      );
                    }
                  );
                  const isTodayFlag = isToday(day);

                  return (
                    <div
                      key={`${dateStr}-${time}`}
                      className={`p-2 text-sm border-r border-[#d3c7bc] min-h-[60px] ${
                        isTodayFlag ? "bg-yellow-200" : ""
                      }`}
                    >
                      {activitiesInTimeSlot.map((activity) => (
                        <button
                          key={activity.id}
                          onClick={() => openActivityDetails(activity)}
                          className="hover:bg-[#e8e1d9]"
                        >
                          <div className="flex items-start gap-2 w-full text-left hover:bg-[#e8e1d9] p-1 rounded transition-colors">
                            <span className="flex-1">{activity.name}</span>
                            <Info className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          </div>
                          <p className="text-start">{activity.startTime}</p>
                        </button>
                      ))}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>

      {/* Chú thích */}
      {/* <div className="mt-4 flex gap-4 text-sm text-[#725b53]">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-[#e8e1d9]"></div>
          <span>Regular Activity</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-[#f3efe9]"></div>
          <span>Time Slot</span>
        </div>
      </div> */}

      {/* Popup chi tiết hoạt động */}
      {selectedActivity && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full">
            <div className="flex justify-between items-start mb-4">
              <h2 className="text-xl font-semibold text-[#725b53]">
                {selectedActivity.name}
              </h2>
              <button
                onClick={closeActivityDetails}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="space-y-2 text-[#725b53]">
              <p>
                <strong>Start Time:</strong> {selectedActivity.startTime}
              </p>
              <p>
                <strong>End Time:</strong> {selectedActivity.endTime}
              </p>
              <p>
                <strong>Date:</strong>{" "}
                {format(new Date(selectedActivity.date), "EEEE, dd.MM.yyyy", {
                  locale: enUS,
                })}
              </p>
              <p>
                <strong>Location:</strong> {selectedActivity.location}
              </p>
              <p>
                <strong>Details:</strong> {selectedActivity.details}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
